import { useCallback } from 'react';
import { useContextSelector } from 'use-context-selector';
import { useTaskCenterMutations, TaskCenterTypes } from '@frontend/api-task-center';
import { useDemoPanelStore, useIsInsideDemoPanel } from '@frontend/demo-panels';
import { useTranslation } from '@frontend/i18n';
import { useForceTray, WIDTH_DIMS } from '@frontend/panel-engine';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { getDemoTaskPanelComponent } from '../demo-task-panel-registry';
import { TaskCenterProviderContext } from '../provider';
import { taskEvents, TaskEvents } from '../task-events';
import { Citation } from '../types';
import { getUpdatedFilters } from '../utils';

interface SetMessageCitationProps {
  sourceId: string;
  source: Citation;
  personName?: string;
  locationId: string;
}

export const useTaskCitation = () => {
  const { t } = useTranslation('task-center');
  const { enableForceTray, disableForceTray } = useForceTray();
  const isInsideDemoPanel = useIsInsideDemoPanel();

  const { getTaskByTaskDetailsMutation } = useTaskCenterMutations();

  const selectedTask = useContextSelector(TaskCenterProviderContext, (state) => state.selectedTask);
  const setSelectedTask = useContextSelector(TaskCenterProviderContext, (state) => state.setSelectedTask);
  const setTaskMode = useContextSelector(TaskCenterProviderContext, (state) => state.setTaskMode);
  const setIsTaskCenterOpen = useContextSelector(TaskCenterProviderContext, (state) => state.setIsTaskCenterOpen);
  const isTaskCenterOpen = useContextSelector(TaskCenterProviderContext, (state) => state.isTaskCenterOpen);
  const selectedLocationIds = useContextSelector(TaskCenterProviderContext, (state) => state.locationIds);
  const setTaskId = useContextSelector(TaskCenterProviderContext, (state) => state.setTaskId);

  const setMessageCitation = ({ sourceId, source, personName, locationId }: SetMessageCitationProps) => {
    if (selectedTask?.sourceId === sourceId && isTaskCenterOpen) {
      // When we have a task already open with the same sourceId, we don't want to do anything on citation click as
      // user is already seeing the relevant task details. This also prevents unnecessary API calls and re-renders.
      return;
    }
    const title = t(`Inbox: New Task ({{personTitle}})`, {
      personTitle: !!personName ? personName : formatPhoneNumber(source.phone_no) ?? 'Unknown User',
    });
    if (!!selectedTask?.sourceId) {
      // marking the selectedTask as null so that we override task the when we API fetches the updated details
      setSelectedTask(null);
    }
    const patientId = source.patient_id;
    setSelectedTask({
      source: TaskCenterTypes.TaskSource.SOURCE_MESSAGING,
      sourceId,
      metadata: JSON.stringify({ source }),
      title,
      locationId: locationId,
      type: TaskCenterTypes.TaskType.TYPE_UNKNOWN,
      ...(patientId ? { patient: { id: patientId, name: personName } } : {}),
    });
    setTaskMode('citation');
    !isTaskCenterOpen && setIsTaskCenterOpen(true);
  };

  const setTaskBySourceId = async (
    { source, sourceId, status, assignee, priority }: Partial<TaskCenterTypes.Task>,
    locationId: string[] = selectedLocationIds
  ) => {
    const filters = getUpdatedFilters({ source, sourceId, status, assignee, priority }, locationId);
    return getTaskByTaskDetailsMutation.mutateAsync(
      { filters, includeMetadata: true },
      {
        onError: (error) => {
          throw Error(String(error));
        },
        onSuccess: (res) => {
          if (res?.tasks && (res.tasks.length > 1 || res.tasks.length === 0)) {
            throw new Error(
              res.tasks.length === 0 ? t('Task not found') : t('Multiple tasks found with same sourceId')
            );
          } else {
            if (res?.tasks?.[0]) {
              setSelectedTask(res.tasks[0]);
              setTaskMode('edit');
              !isTaskCenterOpen && setIsTaskCenterOpen(true);
            }
          }
        },
      }
    );
  };

  const openTaskWithTaskId = (taskId: string, isDemo?: boolean) => {
    setTaskId(taskId);
    setTaskMode('edit');

    const DemoTaskPanelComponent = isInsideDemoPanel ? getDemoTaskPanelComponent() : null;
    if (DemoTaskPanelComponent) {
      // Inside the demo panel stack the regular task center tray would outside or behind the demo drawer, so open the
      // task details as another demo panel instead.
      useDemoPanelStore.getState().register({
        id: 'demo-task-center',
        component: DemoTaskPanelComponent,
        width: WIDTH_DIMS['medium'],
      });
      return;
    }

    if (isTaskCenterOpen) {
      return;
    }
    isDemo ? enableForceTray() : disableForceTray();
    setIsTaskCenterOpen(true);
  };

  const onTaskPanelEvent = useCallback(
    <K extends keyof TaskEvents>(event: K, handler: (payload: TaskEvents[K]) => void) => {
      taskEvents.on(event, handler);
    },
    []
  );

  const offTaskPanelEvent = useCallback(
    <K extends keyof TaskEvents>(event: K, handler: (payload: TaskEvents[K]) => void) => {
      taskEvents.off(event, handler);
    },
    []
  );

  return { setMessageCitation, setTaskBySourceId, openTaskWithTaskId, onTaskPanelEvent, offTaskPanelEvent };
};
